import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined'
import CropFreeIcon from '@mui/icons-material/CropFree'
import InventoryIcon from '@mui/icons-material/Inventory'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import LocalParkingIcon from '@mui/icons-material/LocalParking'
import NotificationsIcon from '@mui/icons-material/Notifications'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { parseCookies, setCookie } from 'nookies'
import { useSnackbar } from 'notistack'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { buildLeadFormWaMessage } from 'src/components/LeadForm/buildLeadFormWaMessage'
import type { LeadFormValues } from 'src/components/LeadForm/useLeadFormState'
import { escapeForHtml } from 'src/utils/escapeForHtml'
import { getClickIdsFromCookies } from 'src/utils/utms'

import PromoBadge, { promoBadgeContent } from 'src/components/Promo/PromoBadge'
import { PillarPage } from 'src/constants/searchPages'
import useLogger from 'src/context/LoggerProvider/useLogger'
import useCommon from 'src/hooks/useCommon'
import usePillarNames from 'src/hooks/usePillarNames'
import { useGetPrice } from 'src/hooks/usePrice'
import useSpaceLabels from 'src/hooks/useSpaceLabels'
import useTrackEvent from 'src/hooks/useTrackEvent'
import { baseFleetVariant, useListingVariant } from 'src/lib/listingVariants'
import type { LeadFormChannel } from 'src/lib/listingVariants/types'
import { cleanAccentLocation } from 'src/utils/address'
import { getDiscount } from 'src/utils/coverPhoto'
import { operatorPromoPill } from 'src/utils/operatorPromo'
import useAddToCart, { useGenerateLead } from 'src/utils/pixels/common'
import { isTraditionalVariant } from 'src/utils/pricing'
import { claimWaSessionToken } from 'src/utils/waSessionToken'
import { buildWhatsappUrl } from 'src/utils/whatsapp'
import {
  AccessAvailability,
  AccessFrequency,
  AccessHours,
  AccessPermission,
  CustomerType,
  DealType,
  IConfiguration,
  ILocation,
  IMutation,
  IMutationSendCustomerInfoEmailArgs,
  Interest,
  LocationType
} from '../../types/graphql'
import { numberRounedUpWithoutDecimals } from '../../utils'
import ModalDrawer from '../ModalDrawer'
import VerifiedBadge from '../VerifiedBadge'
import AdminOwnerPanel from './AdminOwnerPanel'
import ListingPriceFacts from './ListingPriceFacts'
import Location from './Location'
import LocationFeatures from './LocationFeatures'
import Policies from './Policies'
import { getActiveScarcityCount } from './utils'

const SEND_CUSTOMER_INFO_EMAIL = gql`
  mutation SendCustomerInfoEmail($customerInfo: CustomerInfoInput!) {
    sendCustomerInfoEmail(customerInfo: $customerInfo)
  }
`

// Mobile-only fleet trigger lives in this Header (parking + SpotMe Now).
// The desktop version is rendered inside PriceCard's sticky sidebar. Both
// open the unified LeadForm in fleet mode. Gated on `fleetModalOpen` which
// starts false on both SSR and CSR — no hydration mismatch — and the form
// chunk (with libphonenumber-js) stays out of the listing critical path.
const LeadFormDynamic = dynamic(() => import('src/components/LeadForm'))

/** Editorial hairline divider — replaces the previous heavy blue rule. */
const SectionDivider = ({ className = '' }: { className?: string }) => (
  <hr
    className={`my-8 h-px w-full border-0 bg-ink-100/15 md:my-12 ${className}`}
    aria-hidden="true"
  />
)

// `addressDetails.colonia` from Google Geocoding is unreliable (Plus Codes,
// "Sin Nombre" placeholders, raw addresses). Use `cleanAccentLocation` —
// see src/utils/address.ts.

function Header({
  location,
  activeConfig,
  reportsRef
}: {
  location: ILocation
  activeConfig: IConfiguration
  reportsRef: React.RefObject<HTMLDivElement | null>
}) {
  const {
    accessPermission,
    accessFrequency,
    accessHours,
    accessAvailability,
    description,
    shortAddress,
    cardLocation,
    configurations,
    addressDetails,
    isVerified
  } = location
  const { city: cityName, colonia: coloniaName } = addressDetails ?? {}

  const configs = configurations?.filter(
    (c) => !(c.isDeleted || c.isDeactivated || (c.availability ?? 0) <= 0)
  )
  const { size } = activeConfig ?? {}

  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false)
  const [isConfigDrawerOpen, setIsConfigDrawerOpen] = useState(false)
  const [hasConfigScroll, setHasConfigScroll] = useState(false)
  const configContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const checkScroll = () => {
      if (configContainerRef.current) {
        const { scrollHeight, clientHeight } = configContainerRef.current
        setHasConfigScroll(scrollHeight > clientHeight)
      }
    }

    checkScroll()
    window.addEventListener('resize', checkScroll)
    return () => window.removeEventListener('resize', checkScroll)
  }, [configs])

  const { t } = useTranslation('details')
  const {
    ACCESS_AVAILABILITY_MAP,
    ACCESS_FREQUENCY_MAP,
    ACCESS_HOURS_MAP,
    ACCESS_PERMISSION_MAP
  } = useSpaceLabels()

  const AccessItems = [
    {
      text: ACCESS_PERMISSION_MAP[
        accessPermission ?? AccessPermission.NoNotice
      ],
      Icon: NotificationsIcon,
      title: t('HEADER.NOTICE')
    },
    {
      text: ACCESS_FREQUENCY_MAP[accessFrequency ?? AccessFrequency.Anyday],
      Icon: CalendarMonthIcon,
      title: t('HEADER.AVAILABLE_DAYS')
    },
    {
      text: ACCESS_HOURS_MAP[accessHours ?? AccessHours.Anytime],
      Icon: AccessTimeIcon,
      title: t('HEADER.SCHEDULE')
    },
    {
      text: ACCESS_AVAILABILITY_MAP[
        accessAvailability ?? AccessAvailability.Daily
      ],
      Icon: InventoryIcon,
      title: t('HEADER.FREQUENCY')
    }
  ]
  const { configLocMap, sortConfigs } = useCommon()
  const locType = configLocMap(activeConfig)
  const sqm = (size?.length ?? 1) * (size?.width ?? 1)
  // Locale-aware — see usePillarNames; PILLAR_PAGE_MAP carries only Spanish.
  const { searchName } = usePillarNames(activeConfig.pillarPage as PillarPage)

  // Visual truncation is done in CSS via Tailwind `line-clamp` so the
  // server and client render the same HTML. `needsExpansion` uses the
  // SMALLER (mobile) thresholds so mobile users always get the toggle when
  // content overflows; desktop may occasionally show the toggle on content
  // that already fits — clicking it then no-ops visually, which is benign.
  const needsExpansion = useMemo(() => {
    if (!description) return false
    return description.split('\n').length > 4 || description.length > 180
  }, [description])

  const { getPrice, getIsBrokerMode } = useGetPrice()
  const isBrokerMode = getIsBrokerMode(activeConfig)
  const isTraditionalMode = isTraditionalVariant(activeConfig)
  // Cancellation policy + "no living" rule are SpotMe Now contractual terms;
  // broker-mediated and Anexo C (Traditional) contracts have their own terms.
  const hideMonthlyOnlyPolicies = isBrokerMode || isTraditionalMode
  const variant = useListingVariant()
  const { showWatching: watchingFromCount, watchingCount } =
    getActiveScarcityCount(
      activeConfig,
      isBrokerMode,
      location.inventoryIsFresh
    )
  // Gate the watching pill on BOTH (a) the count-derived flag from utils.ts and
  // (b) the variant's showWatching flag — Unit 11 may flip the variant flag off
  // for surfaces that don't want it. Today every variant has showWatching: true,
  // so this is a behavioral no-op that threads the variant through cleanly.
  const showWatching = watchingFromCount && variant.priceCard.showWatching
  // Branch-level demand (distinct people with a lead action, 30 days; null
  // server-side below 3) outranks the per-unit visits count: it is people,
  // not sessions, and it does not split a multi-size branch's demand.
  const recentInquiries = variant.priceCard.showWatching
    ? (location.recentInquiries ?? null)
    : null
  const showDemandPill = recentInquiries !== null || showWatching

  // Mobile-only fleet trigger — resolver-owned verdict since 2026-08-20
  // (Monthly OR Traditional parking + availability over the fleet
  // threshold; see resolveListingVariant.ts → priceCard.showFleetCta).
  // Reading the resolved flag keeps this in lockstep with PriceCard's
  // desktop fleet pill by construction.
  const showFleetTrigger = variant.priceCard.showFleetCta
  const [fleetModalOpen, setFleetModalOpen] = useState(false)
  const handleAddToCart = useAddToCart()
  // Fires GA4/Meta/Bing/LinkedIn/PostHog pixel AND writes LEAD_SUBMIT
  // (subType='business_fleet') to internal ES so marketing-report Excel
  // attributes fleet-pill lead submits to the Parking funnel.
  const generateLead = useGenerateLead()
  const { enqueueSnackbar } = useSnackbar()
  const { trackEvent, EventAction } = useTrackEvent()
  const logger = useLogger()
  const [sendCustomerInfoEmail] = useMutation<
    Pick<IMutation, 'sendCustomerInfoEmail'>,
    IMutationSendCustomerInfoEmailArgs
  >(SEND_CUSTOMER_INFO_EMAIL)

  const handleOnFleetClick = () => {
    // `skipPixelFanout: true` — LeadForm Step 1 useEffect fires the
    // canonical pixel when the fleet modal opens (see PriceCard's
    // `handleOnBrokerClick` for the full rationale). Internal ES
    // `trackEvent` + Clarity still fire here.
    handleAddToCart(
      'broker_contact',
      location,
      activeConfig,
      undefined,
      undefined,
      {
        skipPixelFanout: true
      }
    )
    setFleetModalOpen(true)
  }

  const fleetLeadPrefill = useMemo<Partial<LeadFormValues>>(() => {
    if (typeof window === 'undefined') return {}
    const raw = parseCookies(null).lead
    if (!raw) return {}
    try {
      const lead = JSON.parse(raw) as Record<string, unknown>
      return {
        name: typeof lead.name === 'string' ? lead.name : '',
        phone: typeof lead.phone === 'string' ? lead.phone : '',
        email: typeof lead.email === 'string' ? lead.email : '',
        additionalInfo:
          typeof lead.additionalInfo === 'string' ? lead.additionalInfo : ''
      }
    } catch {
      return {}
    }
  }, [])

  const handleFleetLeadFormSubmit = useCallback(
    async (values: LeadFormValues, channel: LeadFormChannel) => {
      const trimmedName = values.name?.trim() ?? ''
      const trimmedEmail = values.email?.trim() ?? ''
      const trimmedPhone = values.phone?.trim() ?? ''
      try {
        await sendCustomerInfoEmail({
          variables: {
            customerInfo: {
              name: trimmedName,
              lastname: '',
              email: trimmedEmail,
              phone: trimmedPhone,
              message: values.additionalInfo ?? '',
              interest: Interest.Rent,
              customerType: CustomerType.RentBroker,
              locationId: location.id,
              configId: activeConfig?.id,
              locationType: activeConfig?.locationType ?? undefined,
              leadSource: 'listing_fleet_modal',
              quantity:
                values.quantity && values.quantity > 0
                  ? values.quantity
                  : undefined,
              // Injected by LeadForm's BotID pre-flight — server keeps the
              // lead but skips the host notification when flagged.
              botIdFlagged: values.botIdFlagged
            }
          }
        })
      } catch (error) {
        logger.error('Error creating fleet lead', { error })
        enqueueSnackbar(
          'Ha ocurrido un error al enviar la información, por favor intenta de nuevo',
          { variant: 'error' }
        )
        setCookie(null, 'lead', '', { maxAge: 0 })
        throw error
      }

      setCookie(
        null,
        'lead',
        JSON.stringify({
          name: trimmedName,
          email: trimmedEmail,
          phone: trimmedPhone,
          additionalInfo: values.additionalInfo ?? ''
        }),
        { maxAge: 30 * 24 * 60 * 60, path: '/' }
      )

      trackEvent({
        action: EventAction.BrokerSignup,
        locationId: location.id,
        configurationId: activeConfig?.id,
        subType: 'broker_contact'
      })
      generateLead({
        method: 'business_fleet',
        locationType: activeConfig?.locationType ?? undefined,
        dealType: activeConfig?.dealType,
        monthlyPrice: activeConfig?.price ?? undefined,
        channel
      })

      if (channel === 'whatsapp') {
        const msg = buildLeadFormWaMessage({
          variant: { meta: variant.meta, leadForm: variant.leadForm },
          location,
          configuration: activeConfig,
          values: {
            name: trimmedName,
            quantity: values.quantity,
            when: values.when,
            additionalInfo: values.additionalInfo
          },
          locale: 'es',
          sessionToken: claimWaSessionToken(),
          clickIds: getClickIdsFromCookies()
        })
        // v1: all WA leads route to the corporate number for chatbot/triage.
        // Specialist routing returns in a follow-up when sales-ops is
        // provisioned to absorb direct leads.
        const url = buildWhatsappUrl(msg)
        if (typeof window !== 'undefined') {
          window.open(url, '_blank')
        }
      }
    },
    [
      sendCustomerInfoEmail,
      trackEvent,
      EventAction,
      location,
      activeConfig,
      variant.meta,
      variant.leadForm,
      enqueueSnackbar,
      logger
    ]
  )

  /** Renders a single configuration card */
  const renderConfigCard = (config: IConfiguration, isInDrawer = false) => {
    const { id, size, availability } = config ?? {}
    const isSelected = activeConfig?.id === id
    const sqm = (size?.length ?? 1) * (size?.width ?? 1)
    const isParking = config.locationType === LocationType.Parking
    const fprice = getPrice(location, config)
    const icon = isParking ? (
      <LocalParkingIcon className="mt-[-3px] text-lg text-blue-500" />
    ) : null
    const isBrokerMode = getIsBrokerMode(config)
    const discount = getDiscount(config, isBrokerMode)
    // The operator's own promotion (Traditional only, server-gated).
    const promoPill = operatorPromoPill(config.operatorPromo)
    const promoBadge = promoBadgeContent(discount, promoPill)
    const areaPrice = fprice / sqm
    const [iconBig, value, unit] = isBrokerMode
      ? [
          <CropFreeIcon className="mt-[-2.5px] mr-1 text-lg" key="iconBig" />,
          areaPrice,
          '/m²'
        ]
      : [null, fprice, '/mes']
    const { scarcityCount, showScarcity } = getActiveScarcityCount(
      config,
      isBrokerMode,
      location.inventoryIsFresh
    )

    return (
      <Link
        href={config.urlRoute ?? ''}
        key={id}
        onClick={() => isInDrawer && setIsConfigDrawerOpen(false)}
      >
        <div
          role="button"
          tabIndex={0}
          className={`flex w-full shrink-0 flex-col gap-1.5 border px-4 py-3 text-left transition-colors duration-200 md:gap-2 md:p-4
            ${isSelected ? 'border-blue-500 bg-cream-300' : 'border-ink-100/25 bg-white-500 hover:border-blue-500'}`}
          style={{ minWidth: 0 }}
        >
          <div className="flex min-w-0 items-center justify-between">
            <h3 className="truncate text-sm leading-tight font-semibold text-ink-700 md:text-base">
              {configLocMap(config)}
            </h3>
            {/* One template literal: ` $`/`{value}`/` `/`{unit}` were four
                adjacent text nodes, so React emitted <!-- --> boundaries
                between them. A comment-stripping client turns that into a
                text mismatch → #418. `iconBig` stays a real element
                (element↔text emits no boundary). See CLAUDE.md "Trigger H". */}
            <span className="truncate text-sm leading-tight font-bold text-blue-800 md:text-base">
              {iconBig}
              {` $${numberRounedUpWithoutDecimals(value)} ${unit}`}
            </span>
          </div>

          {/* The facts never wrap («4 m²», «Quedan 2») and the promo badge
              is always one line. On mobile the card is ~256px wide — too
              narrow for facts + «Hasta 15% off pago anticipado» (it ran past
              the card edge, 2026-10-28) — so the badge gets its own row
              there; from `md:` it sits at the row's end. */}
          <div className="flex min-w-0 flex-col items-start gap-1.5 md:flex-row md:items-center md:justify-between md:gap-2">
            <div className="flex shrink-0 items-center gap-1.5 md:gap-2">
              {isParking ? (
                icon
              ) : (
                <span className="rounded bg-cream-300 px-1.5 py-0.5 text-xs font-medium whitespace-nowrap text-ink-700 md:rounded-md md:px-2 md:text-sm">
                  {`${numberRounedUpWithoutDecimals(sqm)} m²`}
                </span>
              )}

              {!showScarcity && (availability ?? 0) >= 1 && (
                <span className="text-[11px] whitespace-nowrap text-ink-200 md:text-sm">
                  {`${availability} disp.`}
                </span>
              )}

              {showScarcity && (
                <span className="inline-flex items-center gap-1 rounded-full bg-yellow-200/70 px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap text-ink-700 md:px-2 md:py-1 md:text-xs">
                  <span className="inline-flex size-1 rounded-full bg-yellow-600 md:size-1.5" />
                  {scarcityCount === 1 ? 'Último' : `Quedan ${scarcityCount}`}
                </span>
              )}
            </div>
            {promoBadge && (
              <PromoBadge
                promo={promoBadge}
                placement="inline"
                className="min-w-0"
              />
            )}
          </div>
        </div>
      </Link>
    )
  }

  const renderConfigurations = () => {
    if (!configs?.length || configs.length <= 1) return null

    const sortedConfigs = configs.sort(sortConfigs)
    const needsScroll = configs.length > 3
    const otherConfigsCount = configs.length - 1

    // Get price range for the CTA subtitle
    const prices = sortedConfigs.map((c) => getPrice(location, c))
    const minPrice = Math.min(...prices)
    const maxPrice = Math.max(...prices)
    const priceRange =
      minPrice === maxPrice
        ? `$${numberRounedUpWithoutDecimals(minPrice)}/mes`
        : `$${numberRounedUpWithoutDecimals(minPrice)} - $${numberRounedUpWithoutDecimals(maxPrice)}/mes`

    return (
      <>
        {/* Mobile: Show active config + "see all" button */}
        <div className="md:hidden">
          <div className="mb-4 flex items-baseline justify-between">
            <div>
              <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase">
                Espacio seleccionado
              </p>
            </div>
            <span className="font-display text-[11px] tracking-[0.16em] text-ink-200 uppercase">
              {/* Single template literal — avoids the SSR `<!-- -->` text
                  boundary that comment-stripping clients remove → #418
                  (Trigger H, hydration_diff 2026-07-25). */}
              {`${configs.length} opciones`}
            </span>
          </div>

          {/* Active config card */}
          {renderConfigCard(activeConfig)}

          {/* "See all options" — editorial hairline disclosure, no card chrome */}
          <button
            type="button"
            onClick={() => setIsConfigDrawerOpen(true)}
            className="group mt-3 flex w-full items-center justify-between border-t border-ink-100/25 py-4 text-left"
          >
            <div className="flex flex-col items-start gap-0.5">
              <span className="text-[10px] tracking-[0.16em] text-ink-200 uppercase md:text-[11px]">
                {`Desde ${priceRange}`}
              </span>
              <span className="font-display text-[15px] font-medium text-ink-700 transition-colors group-hover:text-blue-500 md:text-base">
                {otherConfigsCount === 1
                  ? t('HEADER.OTHER_OPTION')
                  : t('HEADER.OTHER_OPTIONS', { count: otherConfigsCount })}
              </span>
            </div>
            <KeyboardArrowDownIcon className="text-ink-200 transition-colors group-hover:text-blue-500" />
          </button>
        </div>

        {/* Mobile: Bottom sheet with all configurations */}
        <ModalDrawer
          title={t('HEADER.AVAILABLE_SPACES')}
          subtitle={t('HEADER.AVAILABLE_SPACES_SUBTITLE')}
          isOpen={isConfigDrawerOpen}
          setIsOpen={setIsConfigDrawerOpen}
          hideMd
        >
          <div className="flex flex-col gap-3 pb-4">
            {sortedConfigs.map((config) => renderConfigCard(config, true))}
          </div>
        </ModalDrawer>

        {/* Desktop: Keep the existing scrollable list */}
        <div className="hidden md:block">
          <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
            {t('SECTIONS.SPACES_EYEBROW')}
          </p>
          <h2 className="mt-3 mb-5 font-display text-[20px] leading-tight font-medium text-ink-700 md:text-[24px]">
            {t('SECTIONS.CHOOSE_SPACE')}
          </h2>
          <div className="relative">
            <div
              ref={configContainerRef}
              className={`flex w-full flex-col gap-3 pr-2 ${
                needsScroll ? 'max-h-[300px] overflow-y-auto pb-8' : ''
              }`}
            >
              {sortedConfigs.map((config) => renderConfigCard(config))}
            </div>
            {/* Gradient overlay to indicate more content */}
            {hasConfigScroll && (
              <div className="pointer-events-none absolute right-2 bottom-0 left-0 h-12 bg-gradient-to-t from-white-500 to-transparent" />
            )}
          </div>
        </div>
      </>
    )
  }

  const accentLocation =
    cleanAccentLocation(coloniaName) ?? cleanAccentLocation(cityName)

  return (
    <div className="flex flex-col">
      {/* Fleet lead-form sheet — independent of PriceCard's LeadForm
          instance. Uses the fleet variant override so the qualifier asks
          for a quantity and routes to the SpotMe Pro pipeline regardless
          of the listing's underlying dealType. */}
      {fleetModalOpen && (
        <ModalDrawer isOpen={fleetModalOpen} setIsOpen={setFleetModalOpen}>
          <LeadFormDynamic
            container="mobile-sheet"
            leadSource="listing_fleet_modal"
            location={location}
            configuration={activeConfig}
            initialValues={fleetLeadPrefill}
            onSubmit={handleFleetLeadFormSubmit}
            onClose={() => setFleetModalOpen(false)}
            variantOverride={{
              meta: {
                dealType: activeConfig?.dealType ?? DealType.Monthly,
                locationType: activeConfig?.locationType ?? LocationType.Parking
              },
              // Sourced from `baseFleetVariant` — the SAME object PriceCard's
              // desktop fleet pill threads in. This block used to be a
              // hand-written copy of that config and had DRIFTED: it kept the
              // pre-2026-05-18 `steps: ['qualifier','contact','success']`
              // while every off-platform variant (broker/traditional/fleet)
              // inverted to contact-first, so the MOBILE fleet form — ~90% of
              // our traffic — asked for quantity before contact and a partial
              // completer left us an unusable lead. Never re-inline this
              // config; extend `variants/fleet.ts` instead.
              leadForm: baseFleetVariant.leadForm
            }}
          />
        </ModalDrawer>
      )}

      {/* Title block — eyebrow + serif H1 with italic location accent.
          Mobile: smaller eyebrow (10px) with tighter tracking, and a
          tighter eyebrow→H1 gap (`mt-1.5`) so the title sits close to
          the gallery and reads as a caption. Desktop keeps the original
          editorial breathing room. */}
      <section>
        <p className="font-display text-[10px] tracking-[0.18em] text-blue-500 uppercase md:text-xs md:tracking-[0.22em]">
          {searchName}
        </p>

        {/* dangerouslySetInnerHTML to avoid React boundary comments (<!-- -->)
            between `{locType}{' '}<span>{accentLocation}</span>`. FB IAB +
            shopping crawlers (GeedoShopProductFinder etc.) strip HTML comments
            → React 19 #418 on EVERY Broker/Traditional listing-detail H1
            (e.g. "Nave Industrial Monterrey"). Was the dominant actionable
            signature — 20 fires/24h across listing pages (Datadog 2026-06-05).
            locType + accentLocation are author/data strings, escaped
            defensively. See CLAUDE.md "Trigger H". */}
        <h1
          className="mt-1.5 font-display text-[26px] leading-[1.1] font-medium tracking-[-0.01em] text-ink-700 md:mt-3 md:text-[40px]"
          dangerouslySetInnerHTML={{
            __html: accentLocation
              ? `${escapeForHtml(locType)} <span class="font-display text-blue-500 italic">${escapeForHtml(accentLocation)}</span>`
              : escapeForHtml(locType)
          }}
        />

        {/* Row 1 — Editorial meta strip: m² · address. Kept
            deliberately short so it never wraps on 320px, even with a
            long city/neighborhood string. Verified moved to the pill row
            below because the compact icon was too subtle for new users
            to recognize as a trust signal. */}
        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[13px] text-ink-200 md:text-sm">
          <span className="inline-flex items-baseline gap-1 text-ink-700">
            <span className="font-display italic">
              {numberRounedUpWithoutDecimals(sqm)}
            </span>
            <span>m²</span>
          </span>
          <span
            aria-hidden="true"
            className="inline-block size-1 rounded-full bg-ink-100"
          />
          {/* Same label as the search card and map popup — a renter who taps
              through from a result must not see the location renamed.
              `shortAddress` is the pre-`cardLocation` fallback. */}
          <span className="truncate">{cardLocation || shortAddress}</span>
        </div>

        {/* Mobile: the price facts the sticky bar no longer stacks (promo,
            IVA totals, «Último disponible», minimum stay) — next to the
            title, where Daniel asked for them (2026-10-27). */}
        <ListingPriceFacts
          location={location}
          activeConfig={activeConfig}
          hasConfigSelector={(configs?.length ?? 0) > 1}
        />

        {/* Row 2 — Info pills (verified, watching) in editorial blue-tinted
            pill language: lowercase, tracking-[0.08em], blue accent icons.
            They read as *status*, not as actions, because nothing about
            them invites a tap. */}
        {(isVerified || showDemandPill) && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {isVerified && (
              <VerifiedBadge verified={isVerified} variant="editorial" />
            )}
            {showDemandPill && (
              <div className="inline-flex items-center gap-2 border border-blue-500/45 bg-blue-500/6 px-3 py-1.5 text-[11px] font-medium tracking-[0.08em] text-ink-700 md:text-[12px]">
                {/* Static dot, no «live» ping: the count is 30 days of
                    visits, not people on the page right now. */}
                <span
                  aria-hidden="true"
                  className="inline-flex size-2 rounded-full bg-blue-500"
                />
                <span>
                  {recentInquiries !== null
                    ? t('HEADER.INQUIRIES', { count: recentInquiries })
                    : t('HEADER.WATCHING', { count: watchingCount ?? 0 })}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Fleet trigger CTA (mobile only) — adopts the editorial CTA
            button language so it reads as a tap, not a label. Same
            border-ink-700 + uppercase + tracking-[0.14em] vocabulary as
            the Ficha Técnica button below, so the two CTAs in the header
            share one visual class — clearly distinct from the info pills
            above. Sits on its own row so it has the room to feel like a
            button, not get lost in the pill cluster. */}
        {showFleetTrigger && (
          <button
            type="button"
            onClick={handleOnFleetClick}
            className="mt-4 inline-flex items-center gap-2 border border-ink-700 px-5 py-2.5 text-[12px] font-medium tracking-[0.14em] text-ink-700 uppercase transition-colors hover:border-blue-500 hover:text-blue-500 md:hidden"
          >
            <i
              aria-hidden="true"
              className="si si-delivery-truck text-[14px]"
            />
            <span>{t('FLEET_TRIGGER.CTA')}</span>
          </button>
        )}

        {/* Broker mode — Ficha técnica CTA, editorial outline */}
        {isBrokerMode && (
          <div className="mt-6">
            <button
              type="button"
              onClick={() => {
                if (reportsRef.current) {
                  const elementTop = reportsRef.current.offsetTop
                  const offset = 250
                  window.scrollTo({
                    top: elementTop - offset,
                    behavior: 'smooth'
                  })
                }
              }}
              className="inline-flex items-center gap-2 border border-ink-700 px-5 py-2.5 text-[12px] font-medium tracking-[0.14em] text-ink-700 uppercase transition-colors hover:border-blue-500 hover:text-blue-500"
            >
              <CloudDownloadOutlinedIcon className="!text-[16px]" />
              Ficha Técnica
            </button>
          </div>
        )}
      </section>

      {/* Configuration Selector */}
      {(configs?.length ?? 0) > 1 && (
        <>
          <SectionDivider />
          <section className="mb-2">{renderConfigurations()}</section>
        </>
      )}

      {/* Admin-only owner bar. Sits directly under the configuration
          selector — high enough in the rail that the admin team hits it
          without hunting, low enough that it never displaces the listing
          itself (it was briefly above the gallery; too intrusive). Gated
          client-side on `currentUser.isAdmin` inside the component, so
          every other viewer gets nothing at all. */}
      <AdminOwnerPanel location={location} />

      {/* Features — owns its own top hairline so it disappears cleanly when
          there are no features (avoids orphan dividers stacking up). */}
      <LocationFeatures activeConfig={activeConfig} />

      <SectionDivider />

      {/* Description — host-supplied and genuinely optional (`Maybe<String>`),
          so the eyebrow + heading + prose collapse together when it's absent
          rather than rendering an empty labelled block. The pillar link below
          is not host-dependent, so it always stays. */}
      <section>
        {description && (
          <>
            <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
              {t('SECTIONS.DESCRIPTION_EYEBROW')}
            </p>
            <h2 className="sr-only">{t('SECTIONS.DESCRIPTION_EYEBROW')}</h2>
            <div className="relative mt-4">
              <p
                className={`text-[14px] leading-relaxed whitespace-pre-line text-ink-200 md:text-[15px] ${
                  isDescriptionExpanded ? '' : 'line-clamp-4 md:line-clamp-5'
                }`}
              >
                {description}
              </p>
              {needsExpansion && (
                <button
                  type="button"
                  onClick={() =>
                    setIsDescriptionExpanded(!isDescriptionExpanded)
                  }
                  className="mt-3 text-[12px] font-medium tracking-[0.14em] text-ink-700 uppercase underline decoration-blue-500/40 underline-offset-[6px] transition-colors hover:text-blue-500 hover:decoration-blue-500"
                >
                  {isDescriptionExpanded
                    ? t('HEADER.VIEW_LESS')
                    : t('HEADER.VIEW_MORE')}
                </button>
              )}
            </div>
          </>
        )}

        <Link
          href={`/${activeConfig.pillarPage}`}
          className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-medium text-blue-500 underline decoration-blue-500/40 decoration-1 underline-offset-[6px] transition-colors hover:text-blue-700 hover:decoration-blue-700"
        >
          {searchName}
        </Link>
      </section>

      {/* Access — hidden in broker mode */}
      {!isBrokerMode && (
        <>
          <SectionDivider />
          <section>
            <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
              {t('SECTIONS.ACCESS_EYEBROW')}
            </p>
            <h2 className="mt-3 font-display text-[20px] leading-tight font-medium text-ink-700 md:text-[24px]">
              {t('SECTIONS.WHEN_ENTER')}
            </h2>
            <p className="mt-3 max-w-md text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
              {t('SECTIONS.ACCESS_SUBTITLE')}
            </p>

            <dl className="mt-6 grid grid-cols-1 gap-x-6 gap-y-5 border-t border-ink-100/15 pt-6 md:grid-cols-2">
              {AccessItems.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <item.Icon
                    sx={{ fontSize: 18 }}
                    className="mt-0.5 text-blue-500"
                  />
                  <div className="flex flex-col gap-0.5">
                    <dt className="text-[10px] tracking-[0.16em] text-ink-200 uppercase md:text-[11px]">
                      {item.title}
                    </dt>
                    <dd className="text-[14px] text-ink-700 md:text-[15px]">
                      {item.text}
                    </dd>
                  </div>
                </div>
              ))}
            </dl>
          </section>
        </>
      )}

      {/* Cancellation — SpotMe Now only. Broker (B2B) and Traditional
          (Anexo C) contracts have their own cancellation terms negotiated
          off-platform, so this section is hidden in those modes. */}
      {!hideMonthlyOnlyPolicies && (
        <>
          <SectionDivider />
          <section>
            <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
              {t('HEADER.CANCELLATION')}
            </p>
            <h2 className="sr-only">{t('HEADER.CANCELLATION')}</h2>
            <div className="mt-4 flex items-start gap-3 border-l-2 border-blue-500 bg-cream-300 px-5 py-4">
              <CheckCircleIcon
                sx={{ fontSize: 18 }}
                className="mt-0.5 shrink-0 text-blue-500"
              />
              <p className="text-[14px] leading-relaxed text-ink-700 md:text-[15px]">
                {t('HEADER.FULL_REFUND')}
              </p>
            </div>
          </section>
        </>
      )}

      {/* Policies — same SpotMe Now scope as Cancellation. Anexo C contracts
          have their own clauses; do not surface "no living in space" rule there. */}
      {!hideMonthlyOnlyPolicies && (
        <>
          <SectionDivider />
          <Policies />
        </>
      )}

      {/* Location/Map — always renders, so always gets a divider above it
          (regardless of broker mode). */}
      <SectionDivider />
      <Location location={location} isBrokerMode={isBrokerMode} />
    </div>
  )
}

export default React.memo(Header)
